<template>
  <div
    :dir="layoutDir"
    class="flex min-h-screen w-full flex-col bg-[#0d0f15] text-white p-2"
  >
    <header class="relative z-10 h-20 border-b border-white/5 bg-[#0d0f15]">
      <div
        class="mx-auto flex h-full max-w-5xl items-center justify-between px-4"
      >
        <div
          class="text-2xl font-black tracking-tight text-white p-2 rounded-full border border-white/8 cursor-pointer"
          @click="$router.go(-1)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="white"
            class="h-6 w-6"
            :class="isArabic ? 'rotate-180' : ''"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
            />
          </svg>
        </div>

        <InitHeader
          :currentLanguage="currentLanguage"
          @language-changed="setLanguage"
        />
      </div>
    </header>
    <div class="w-full max-w-5xl flex flex-col mx-auto mt-24">
      <template v-if="viewState === 'success'">
        <BokSuccess
          :currentLanguage="currentLanguage"
          :details="resultDetails"
        />
      </template>

      <template v-else-if="viewState === 'failure'">
        <BokFailure
          :currentLanguage="currentLanguage"
          :errorMessage="failureMessage"
          @retry="retryVerification"
          @change-number="goToChangeNumber"
        />
      </template>

      <template v-else>
        <h1 class="text-white text-xl font-semibold">{{ t.complete_sub }}</h1>

        <section
          class="md:mx-auto w-full my-3 rounded-xl border border-[#343743] bg-[#17191f]/95 p-4 shadow-2xl sm:p-6 md:p-8"
          style="background: rgb(24, 25, 31)"
        >
          <form class="mt-5 space-y-4" @submit.prevent="submit">
            <div>
              <label
                for="otp"
                class="block text-lg font-bold text-white space-y-3"
                >{{ t.enter_otp }}</label
              >
              <small class="text-white space-y-3">
                {{ t.otp_sent_to }}
              </small>
              <div
                dir="ltr"
                class="flex overflow-hidden rounded-lg border border-[#343743] bg-[#1d1f28] focus-within:border-[#d0b45e] w-full my-3"
              >
                <input
                  id="otp"
                  v-model="otp"
                  type="tel"
                  maxlength="9"
                  pattern="[0-9]*"
                  placeholder="- - - - -"
                  class="w-full rounded-lg text-xl text-white bg-transparent px-4 py-4 placeholder:text-gray-100 placeholder:text-3xl outline-none focus:border-transparent focus:outline-none focus:ring-0 focus:ring-transparent focus:shadow-none"
                  @input="sanitizeOtp"
                />
              </div>
            </div>
            <div class="flex justify-between">
              <small class="text-white">
                {{ t.otp_question }}
              </small>

              <button
                type="button"
                @click="resendOtp"
                :disabled="!canResend || isResending"
                class="text-[#d0b45e] underline transition"
                :class="
                  !canResend || isResending
                    ? 'cursor-not-allowed opacity-60 no-underline'
                    : 'cursor-pointer'
                "
              >
                {{ resendButtonText }}
              </button>
            </div>
            <button
              type="submit"
              :disabled="!validOtp || isSubmitting"
              class="w-full rounded-lg py-3.5 text-sm font-bold transition active:scale-[0.99]"
              :class="
                validOtp && !isSubmitting
                  ? 'bg-[#d0b45e] text-[#17181d] hover:bg-[#dec46e]'
                  : 'cursor-not-allowed bg-[#7f7344] text-[#1b1d22]'
              "
            >
              {{ isSubmitting ? t.submitting : t.confirm_subscription }}
            </button>
            <div class="flex justify-center">
              <small
                class="text-gray-500 text-center cursor-pointer"
                @click="goToChangeNumber"
                >{{ t.edit_phone }}</small
              >
            </div>
          </form>
          <footer
            class="flex justify-between border-t border-white/10 py-3 mt-6"
          >
            <img
              src="@/assets/nasna-logo-bok.svg"
              alt="nasna-logo"
              class="h-8 w-auto object-cover"
            />
            <img
              src="@/assets/payments/bok.png"
              alt="bok-logo"
              class="h-8 object-cover"
            />
          </footer>
        </section>
      </template>
    </div>
  </div>
</template>

<script>
import { verifyTrs } from "@/bok.trs";
import InitHeader from "@/components/bok/InitHeader.vue";
import BokSuccess from "@/views/auth/bok/BokSuccess.vue";
import BokFailure from "@/views/auth/bok/BokFailure.vue";
import { useBokStore } from "@/stores/bok";
import { mapActions } from "pinia";
import { initTrs } from "@/bok.trs";

import { useToast } from "vue-toastification";

export default {
  components: {
    InitHeader,
    BokSuccess,
    BokFailure,
  },
  data() {
    return {
      otp: "",
      toast: useToast(),
      currentLanguage: localStorage.getItem("lang-bok") || "ar",
      translations: verifyTrs,
      isSubmitting: false,
      isResending: false,
      viewState: "verify",
      failureMessage: "",
      resendSecondsLeft: 30,
      resendCooldownSeconds: 30,
      resendAttempts: 0,
      maxResendAttempts: 3,
      resendTimerId: null,
      resultDetails: {
        phone: localStorage.getItem("phone-bok") || "",
        cif: localStorage.getItem("cif-bok") || "",
        price: localStorage.getItem("bok-price") || "9,800.00",
      },
    };
  },
  computed: {
    validOtp() {
      return this.otp.length === 6; // Assuming OTP is 6 digits long
    },
    layoutDir() {
      return this.isArabic ? "rtl" : "ltr";
    },
    isArabic() {
      return this.currentLanguage === "ar";
    },
    t() {
      return this.translations[this.currentLanguage];
    },
    canResend() {
      return (
        this.resendSecondsLeft === 0 &&
        this.resendAttempts < this.maxResendAttempts &&
        !this.isResending &&
        this.viewState === "verify"
      );
    },
    resendButtonText() {
      if (this.resendAttempts >= this.maxResendAttempts) {
        return this.t.resend_limit;
      }

      if (this.resendSecondsLeft > 0) {
        return this.t.resend_in.replace("{seconds}", this.resendSecondsLeft);
      }

      if (this.isResending) {
        return this.t.submitting;
      }

      return this.t.resend_otp;
    },
  },
  methods: {
    ...mapActions(useBokStore, ["verifySubscription", "resendSubscriptionOtp"]),
    createIdempotencyKey(prefix = "bok") {
      const randomUuid =
        typeof window !== "undefined" &&
        window.crypto &&
        typeof window.crypto.randomUUID === "function"
          ? window.crypto.randomUUID()
          : null;

      if (randomUuid) {
        return `${prefix}-${randomUuid}`;
      }

      return `${prefix}-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 10)}`;
    },
    getBokIdempotencyStorageKeys() {
      return {
        CAPTURE: "bok-idempotency-capture",
        RESEND: "bok-idempotency-resend",
        INIT: "bok-idempotency-init",
      };
    },
    clearBokIdempotencyKeys() {
      const keys = this.getBokIdempotencyStorageKeys();
      localStorage.removeItem(keys.INIT);
      localStorage.removeItem(keys.CAPTURE);
      localStorage.removeItem(keys.RESEND);
    },
    setLanguage(language) {
      this.currentLanguage = language;
      localStorage.setItem("lang-bok", language);
    },
    getPaymentErrorMessage(error) {
      const data = error?.response?.data || error?.data || error || {};
      const message =
        typeof data.message === "string" ? data.message.trim() : "";
      const locale = this.currentLanguage.toLowerCase().startsWith("en")
        ? "en"
        : "ar";
      const paymentErrors = initTrs[locale].paymentErrors;

      if (message.includes("Please enable Ecommerce on Bankak")) {
        return paymentErrors.BOK_ECOMMERCE_DISABLED;
      }

      return (
        paymentErrors[data.errorCode?.trim()] ||
        message ||
        this.translations[locale].verificationError
      );
    },
    sanitizeOtp() {
      this.otp = this.otp.replace(/\D/g, "").slice(0, 9);
    },
    startResendCooldown(seconds = this.resendCooldownSeconds) {
      if (this.resendTimerId) {
        clearInterval(this.resendTimerId);
      }

      this.resendSecondsLeft = seconds;
      this.resendTimerId = setInterval(() => {
        if (this.resendSecondsLeft <= 1) {
          clearInterval(this.resendTimerId);
          this.resendTimerId = null;
          this.resendSecondsLeft = 0;
          return;
        }

        this.resendSecondsLeft -= 1;
      }, 1000);
    },
    async resendOtp() {
      if (!this.canResend) {
        return;
      }

      const keyStorage = this.getBokIdempotencyStorageKeys();
      const resendIdempotencyKey =
        localStorage.getItem(keyStorage.RESEND) ||
        this.createIdempotencyKey("bok-resend-flow");
      localStorage.setItem(keyStorage.RESEND, resendIdempotencyKey);

      try {
        this.isResending = true;
        const response = await this.resendSubscriptionOtp(
          {},
          { idempotencyKey: resendIdempotencyKey }
        );
        if (response?.data?.success === false) {
          this.toast.error(this.getPaymentErrorMessage(response));
          return;
        }

        this.resendAttempts += 1;
        this.otp = "";
        this.startResendCooldown();
        localStorage.removeItem(keyStorage.RESEND);
        this.toast.success(this.t.resend_success);
      } catch (error) {
        console.error("BOK resend OTP failed:", error);
        this.toast.error(this.getPaymentErrorMessage(error));
      } finally {
        this.isResending = false;
      }
    },
    retryVerification() {
      this.otp = "";
      this.failureMessage = "";
      const keyStorage = this.getBokIdempotencyStorageKeys();
      localStorage.removeItem(keyStorage.CAPTURE);
      this.viewState = "verify";
    },
    goToChangeNumber() {
      this.clearBokIdempotencyKeys();
      this.$router.push({ name: "init.payment" });
    },
    async submit() {
      if (!this.validOtp || this.isSubmitting) {
        return;
      }

      const payload = {
        otp: this.otp,
      };

      const keyStorage = this.getBokIdempotencyStorageKeys();
      const captureIdempotencyKey =
        localStorage.getItem(keyStorage.CAPTURE) ||
        this.createIdempotencyKey("bok-capture-flow");
      localStorage.setItem(keyStorage.CAPTURE, captureIdempotencyKey);

      try {
        this.isSubmitting = true;
        const response = await this.verifySubscription(payload, {
          idempotencyKey: captureIdempotencyKey,
        });
        if (response?.data?.success) {
          const serverPhone =
            response?.data?.mobile || this.resultDetails.phone;
          const serverCif = response?.data?.cif || this.resultDetails.cif;
          const serverPrice =
            response?.data?.price ||
            response?.data?.amount ||
            this.resultDetails.price;

          this.resultDetails = {
            phone: serverPhone,
            cif: serverCif,
            price: String(serverPrice),
          };
          this.clearBokIdempotencyKeys();
          this.viewState = "success";
          setTimeout(() => {
            this.$router.push({ name: "home" });
          }, 3000);
        } else {
          localStorage.removeItem(keyStorage.CAPTURE);
          this.failureMessage = this.getPaymentErrorMessage(response);
          this.viewState = "failure";
        }
      } catch (error) {
        console.error("BOK verification failed:", error);
        this.failureMessage = this.getPaymentErrorMessage(error);
        this.viewState = "failure";
      } finally {
        this.isSubmitting = false;
      }
    },
  },
  created() {
    this.startResendCooldown();
  },
  beforeUnmount() {
    if (this.resendTimerId) {
      clearInterval(this.resendTimerId);
      this.resendTimerId = null;
    }
  },
};
</script>

<style scoped>
/* Your existing styles */
</style>

<style scoped>
.width-height {
  width: 900px;
  height: auto;
}

.xxsm {
  width: 255px;
}

.w-btn {
  width: 290px;
  align-self: right;
}

.btn-color {
  background-color: #9d2890;
}

.bg-sub {
  background-color: #181b30;
}

input {
  text-align: center;
  outline: none;
  padding-left: 5px;
  color: aliceblue;
}

::-webkit-input-placeholder {
  text-align: center;
}

:-moz-placeholder {
  text-align: right;
}

.font-small {
  font-size: 16px;
}
</style>
